/**
 * CaseSendEmailModal
 *
 * Thin wrapper around SingleEmailModal that provides collection-case-specific
 * props (source type, linked transaction IDs for invoice attachment fetching).
 *
 * Follows the exact same pattern as TransactionSendCopyInvoiceModal.
 */

import { useCallback } from "react";
import {
  SingleEmailModal,
  type EmailAttachment,
} from "@/components/shared/single-email-modal";
import { useTransactionDocumentDownload } from "@/hooks/useTransactionDocumentDownload";
import { useCaseTransactionIds } from "@/hooks/useCaseTransactionLinks";
import { useLogCaseEmail } from "@/hooks/useLogCaseEmail";
import { toast } from "sonner";

export interface CaseSendEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string;
  customerId: string;
  customerName?: string;
  accountNumber?: string;
  onSuccess?: () => void;
}

export function CaseSendEmailModal({
  open,
  onOpenChange,
  caseId,
  customerId,
  customerName = "",
  accountNumber = "",
  onSuccess,
}: Readonly<CaseSendEmailModalProps>) {
  const { downloadDocumentAsBase64 } = useTransactionDocumentDownload();
  const { data: linkedTransactionIds = [] } = useCaseTransactionIds(open ? caseId : undefined);
  const logCaseEmail = useLogCaseEmail();

  const handleFetchAttachments = useCallback(
    async (
      onProgress: (done: number, total: number) => void,
    ): Promise<EmailAttachment[]> => {
      if (linkedTransactionIds.length === 0) return [];

      onProgress(0, linkedTransactionIds.length);

      const results = await Promise.allSettled(
        linkedTransactionIds.map(async (txId, idx) => {
          const doc = await downloadDocumentAsBase64(txId);
          onProgress(idx + 1, linkedTransactionIds.length);
          return doc;
        }),
      );

      const successful = results
        .filter(
          (r): r is PromiseFulfilledResult<EmailAttachment> =>
            r.status === "fulfilled",
        )
        .map((r) => r.value);

      const failedCount = results.filter(
        (r) => r.status === "rejected",
      ).length;
      if (failedCount > 0) {
        toast.warning(
          `${failedCount} invoice document(s) could not be downloaded`,
        );
      }

      return successful;
    },
    [linkedTransactionIds, downloadDocumentAsBase64],
  );

  const handleAfterSend = useCallback(
    (payload: {
      toEmails: string[];
      ccEmails: string[];
      subject: string;
      htmlBody: string;
      templateId?: string;
      templateName?: string;
      isSubjectModified: boolean;
      isBodyModified: boolean;
      attachOutstandingInvoices: boolean;
    }) => {
      logCaseEmail.mutate(
        {
          sourceId: caseId,
          toRecipients: payload.toEmails,
          ccRecipients: payload.ccEmails.length > 0 ? payload.ccEmails : undefined,
          subject: payload.subject,
          htmlBody: payload.htmlBody,
          templateId: payload.templateId ? parseInt(payload.templateId, 10) : undefined,
          templateName: payload.templateName,
          isSubjectModified: payload.isSubjectModified,
          isBodyModified: payload.isBodyModified,
          attachOutstandingInvoices: payload.attachOutstandingInvoices,
        },
        {
          onError: () => {
            toast.error("Email was sent but failed to save to history. Please contact support if the issue persists.");
          },
        },
      );
    },
    [logCaseEmail, caseId],
  );

  return (
    <SingleEmailModal
      open={open}
      onOpenChange={onOpenChange}
      title="Send Email"
      customerId={customerId}
      customerName={customerName}
      accountNumber={accountNumber}
      sourceType="collection"
      sourceId={Number(caseId)}
      callbackEventName="creditcontrol:case-email-done"
      attachLabel="Attach outstanding invoices"
      fetchAttachments={handleFetchAttachments}
      onAfterSend={handleAfterSend}
      onSuccess={onSuccess}
      allowedCategoryTypes={["collections", "general"]}
      allowedTemplateTypes={["email"]}
    />
  );
}
